import { c as _c } from "react/compiler-runtime";
import { head } from "es-toolkit/array";
import { readFragment } from "gql.tada";
import { useCallback, useEffect, useState } from "react";
import { useTranslations } from "use-intl";
import { useSnapshot } from "valtio";
import { ActionTimeline, ActionTimelineError, ActionTimelineSkeleton } from "../../../../../components/ActionTimeline";
import { CrossEcosystemSwapNotification } from "../../../../../components/CrossEcosystemSwapNotification";
import { PriceChangeModal, PriceChangeWarning } from "../../../../../components/PriceChange";
import { useAddressCompatibility } from "../../../../../hooks/useAddressCompatibility";
import { useAddress } from "../../../../../polychain/hooks";
import { useTrackEvent } from "../../../../../providers/TrackEventProvider";
import { FlowActions, FlowState } from "../../../../../stores/flowStore";
import { useActionErrors } from "../../../../hooks/useErrors";
import { useCardCheckout } from "../../../../PaymentSection/use-card-checkout";
import { itemQuantitiesForQuote } from "../../../../utils/itemQuantitiesForQuote";
import { useBuyItemQuery } from "../../hooks/useBuyItemQuery";
import { CheckoutTimeline_quoteFragment } from "./CheckoutTimeline.fragment";
export function CheckoutTimeline(t0) {
  const $ = _c(53);
  const {
    quote: quoteKey,
    priceValidation,
    selectedCurrency,
    selectedCurrencyLoading,
    walletDestination,
    onAllActionsCompleted: onAllActionsCompletedProp
  } = t0;
  const t = useTranslations("wallet.CheckoutTimeline");
  const INSUFFICIENT_FUNDS_ERROR = t("insufficientFundsError");
  const {
    flow
  } = useSnapshot(FlowState);
  flow?.isClaim ?? false;
  const actionError = useActionErrors();
  const currentAddress = useAddress();
  const quote = readFragment(CheckoutTimeline_quoteFragment, quoteKey);
  const chainIdentifier = quote.totalPrice.pricePerToken[0]?.token.chain.identifier;
  const isAddressCompatible = useAddressCompatibility(walletDestination?.address ?? currentAddress, chainIdentifier);
  const itemQuantities = itemQuantitiesForQuote(quote);
  const shouldSkipBuyItemQuery = itemQuantities.length === 1 && !itemQuantities[0].pricePerItem;
  const crossChainRequirements = actionError?.includes(INSUFFICIENT_FUNDS_ERROR) && quote.supportsRelayerFulfillment?.crossChain;
  const [userAcknowledgedPriceChange, setUserAcknowledgedPriceChange] = useState(false);
  const t1 = flow?.type === "checkout" ? flow.slippage : undefined;
  const t2 = walletDestination?.address;
  const t3 = Boolean(quote.supportsRelayerFulfillment?.crossChain);
  const t4 = shouldSkipBuyItemQuery || crossChainRequirements || priceValidation.hasPriceChanged && !userAcknowledgedPriceChange || !isAddressCompatible || selectedCurrencyLoading;
  let t5;
  if ($[0] !== itemQuantities || $[1] !== selectedCurrency || $[2] !== t1 || $[3] !== t2 || $[4] !== t3 || $[5] !== t4) {
    t5 = {
      itemQuantities,
      slippage: t1,
      currency: selectedCurrency,
      recipient: t2,
      supportsCrossChain: t3,
      pause: t4
    };
    $[0] = itemQuantities;
    $[1] = selectedCurrency;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const {
    actions,
    error,
    fetching,
    refetch
  } = useBuyItemQuery(t5);
  let t6;
  let t7;
  if ($[7] !== chainIdentifier || $[8] !== selectedCurrency) {
    t6 = () => {
      if (selectedCurrency) {
        const isCross = selectedCurrency.chainIdentifier !== chainIdentifier;
        FlowActions.setFlowField("crossChain", isCross);
      }
    };
    t7 = [selectedCurrency, chainIdentifier];
    $[7] = chainIdentifier;
    $[8] = selectedCurrency;
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  useEffect(t6, t7);
  let t8;
  if ($[11] !== onAllActionsCompletedProp) {
    t8 = (response, receiptTransactions) => {
      if (response.action === "TRANSACTION") {
        FlowActions.setFlowField("transactionIdentifier", {
          chain: response.transactionIdentifier.chain,
          transactionHash: response.transactionIdentifier.hash
        });
      }
      onAllActionsCompletedProp?.(response, receiptTransactions);
    };
    $[11] = onAllActionsCompletedProp;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const onAllActionsCompleted = t8;
  const cardCheckout = useCardCheckout();
  let t9;
  if ($[13] !== cardCheckout || $[14] !== flow || $[15] !== refetch) {
    t9 = () => {
      if (flow?.type === "checkout" && flow.paymentMethod === "card") {
        cardCheckout();
        return;
      }
      refetch();
    };
    $[13] = cardCheckout;
    $[14] = flow;
    $[15] = refetch;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const handleRetry = t9;
  const [showPriceChangeModal, setShowPriceChangeModal] = useState(false);
  const trackEvent = useTrackEvent();
  let t10;
  if ($[17] !== priceValidation || $[18] !== trackEvent) {
    t10 = () => {
      trackEvent("Inline Price Changed Warning Confirmed", {
        priceValidation,
        flow: "checkout"
      });
      setShowPriceChangeModal(true);
    };
    $[17] = priceValidation;
    $[18] = trackEvent;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const onInlineConfirm = t10;
  let t11;
  if ($[20] !== priceValidation || $[21] !== trackEvent) {
    t11 = () => {
      trackEvent("Modal Price Changed Warning Confirmed", {
        priceValidation,
        flow: "checkout"
      });
      setUserAcknowledgedPriceChange(true);
      setShowPriceChangeModal(false);
    };
    $[20] = priceValidation;
    $[21] = trackEvent;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const onModalConfirm = t11;
  if (fetching || selectedCurrencyLoading) {
    let t12;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <ActionTimelineSkeleton count={1} />;
      $[23] = t12;
    } else {
      t12 = $[23];
    }
    return t12;
  }
  if (!(isAddressCompatible || shouldSkipBuyItemQuery)) {
    let t12;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <CrossEcosystemSwapNotification />;
      $[24] = t12;
    } else {
      t12 = $[24];
    }
    return t12;
  }
  if (priceValidation.hasPriceChanged && !userAcknowledgedPriceChange) {
    let t12;
    if ($[25] !== onInlineConfirm || $[26] !== priceValidation) {
      t12 = <PriceChangeWarning onContinue={onInlineConfirm} priceValidation={priceValidation} />;
      $[25] = onInlineConfirm;
      $[26] = priceValidation;
      $[27] = t12;
    } else {
      t12 = $[27];
    }
    const t13 = showPriceChangeModal && <PriceChangeModal onCancel={() => setShowPriceChangeModal(false)} onConfirm={onModalConfirm} priceValidation={priceValidation} symbol={head(quote.totalPrice.pricePerToken)?.token.symbol} />;
    let t14;
    if ($[28] !== t12 || $[29] !== t13) {
      t14 = <>{t12}{t13}</>;
      $[28] = t12;
      $[29] = t13;
      $[30] = t14;
    } else {
      t14 = $[30];
    }
    return t14;
  }
  if (error) {
    let t12;
    if ($[31] !== t) {
      t12 = t("somethingWentWrong");
      $[31] = t;
      $[32] = t12;
    } else {
      t12 = $[32];
    }
    let t13;
    if ($[33] !== handleRetry || $[34] !== t12) {
      t13 = <ActionTimelineError message={t12} onRetry={handleRetry} />;
      $[33] = handleRetry;
      $[34] = t12;
      $[35] = t13;
    } else {
      t13 = $[35];
    }
    return t13;
  }
  if (actionError) {
    let t12;
    if ($[36] !== actionError || $[37] !== handleRetry) {
      t12 = <ActionTimelineError message={actionError} onRetry={handleRetry} />;
      $[36] = actionError;
      $[37] = handleRetry;
      $[38] = t12;
    } else {
      t12 = $[38];
    }
    return t12;
  }
  const T0 = ActionTimeline;
  let t12;
  if ($[39] !== actions.length || $[40] !== t) {
    t12 = actions.length === 0 ? t("noActions") : undefined;
    $[39] = actions.length;
    $[40] = t;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  const t13 = actionError?.includes(INSUFFICIENT_FUNDS_ERROR) ? itemQuantities[0].item.chain : undefined;
  let t14;
  if ($[42] !== actions.length || $[43] !== handleRetry || $[44] !== shouldSkipBuyItemQuery) {
    t14 = () => {
      if (actions.length === 0 && shouldSkipBuyItemQuery) {
        return;
      }
      handleRetry();
    };
    $[42] = actions.length;
    $[43] = handleRetry;
    $[44] = shouldSkipBuyItemQuery;
    $[45] = t14;
  } else {
    t14 = $[45];
  }
  let t15;
  if ($[46] !== T0 || $[47] !== actions || $[48] !== onAllActionsCompleted || $[49] !== t12 || $[50] !== t13 || $[51] !== t14) {
    t15 = <T0 actions={actions} errorMessage={t12} insufficientFundsOnChain={t13} onAllActionsCompleted={onAllActionsCompleted} onReload={t14} />;
    $[46] = T0;
    $[47] = actions;
    $[48] = onAllActionsCompleted;
    $[49] = t12;
    $[50] = t13;
    $[51] = t14;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  return t15;
}